import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  BookOpen, Plus, Sun, Moon, Sunset, Calendar, User,
  AlertTriangle, Trash2, ChevronDown
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const shiftConfig = {
  manana: { label: "Mañana", icon: Sun, bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", time: "06:00 - 14:00" },
  tarde: { label: "Tarde", icon: Sunset, bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", time: "14:00 - 22:00" },
  noche: { label: "Noche", icon: Moon, bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", time: "22:00 - 06:00" },
};

const priorityConfig = {
  normal: { label: "Normal", bg: "bg-slate-50", text: "text-slate-600" },
  importante: { label: "Importante", bg: "bg-amber-50", text: "text-amber-700" },
  urgente: { label: "Urgente", bg: "bg-rose-50", text: "text-rose-600" },
};

export default function DailyLogsPage() {
  const { user } = useAuth();
  const canCreate = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("cargar_registros");

  const [logs, setLogs] = useState([]);
  const [residents, setResidents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedShift, setSelectedShift] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    shift_type: "manana",
    log_type: "general",
    resident_id: "",
    content: "",
    priority: "normal"
  });

  const fetchData = useCallback(async () => {
    try {
      const params = { date: selectedDate };
      if (selectedShift !== "all") params.shift_type = selectedShift;
      const [logsRes, residentsRes] = await Promise.all([
        api.get("/daily-logs", { params }),
        api.get("/residents")
      ]);
      setLogs(logsRes.data);
      setResidents(residentsRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedDate, selectedShift]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.log_type === "general") {
        delete payload.resident_id;
      }
      await api.post("/daily-logs", payload);
      toast.success("Registro creado correctamente");
      setDialogOpen(false);
      setForm({ shift_type: "manana", log_type: "general", resident_id: "", content: "", priority: "normal" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleDelete = async (logId) => {
    try {
      await api.delete(`/daily-logs/${logId}`);
      toast.success("Registro eliminado");
      fetchData();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const groupedByShift = {};
  for (const key of ["manana", "tarde", "noche"]) {
    if (selectedShift === "all" || selectedShift === key) {
      groupedByShift[key] = logs.filter(l => l.shift_type === key);
    }
  }

  const getResidentName = (rid) => residents.find(r => r.id === rid)?.name || "—";

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="daily-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">
            Libro de Novedades
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Registros diarios por turno
          </p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold"
                data-testid="add-daily-log-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Nueva Novedad
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold text-slate-900 font-[Manrope]">
                  Registrar Novedad
                </DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">
                  Agregar una entrada al libro de novedades del turno
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Turno</Label>
                    <Select value={form.shift_type} onValueChange={v => setForm({ ...form, shift_type: v })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="log-shift-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manana">Mañana</SelectItem>
                        <SelectItem value="tarde">Tarde</SelectItem>
                        <SelectItem value="noche">Noche</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Tipo</Label>
                    <Select value={form.log_type} onValueChange={v => setForm({ ...form, log_type: v, resident_id: "" })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="log-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="residente">Por Residente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {form.log_type === "residente" && (
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Residente</Label>
                    <Select value={form.resident_id} onValueChange={v => setForm({ ...form, resident_id: v })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="log-resident-select">
                        <SelectValue placeholder="Seleccionar residente..." />
                      </SelectTrigger>
                      <SelectContent>
                        {residents.map(r => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name} — Hab. {r.room_number || "S/N"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Prioridad</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="log-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="importante">Importante</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Novedad</Label>
                  <Textarea
                    value={form.content}
                    onChange={e => setForm({ ...form, content: e.target.value })}
                    placeholder="Describir la novedad del turno..."
                    className="rounded-xl bg-slate-50 min-h-[100px] border-slate-200"
                    required
                    data-testid="log-content-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={!form.content.trim()}
                  className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all"
                  data-testid="submit-log-btn"
                >
                  Registrar Novedad
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Label className="font-medium text-sm text-slate-700">Fecha:</Label>
          <Input
            type="date"
            value={selectedDate}
            onChange={e => { setSelectedDate(e.target.value); setLoading(true); }}
            className="rounded-xl h-10 bg-slate-50 border-slate-200 w-auto"
            data-testid="logs-date-input"
          />
          <Select value={selectedShift} onValueChange={v => { setSelectedShift(v); setLoading(true); }}>
            <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200 w-[180px]" data-testid="logs-shift-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los turnos</SelectItem>
              <SelectItem value="manana">Mañana</SelectItem>
              <SelectItem value="tarde">Tarde</SelectItem>
              <SelectItem value="noche">Noche</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400">
            {new Date(selectedDate + "T12:00:00").toLocaleDateString("es-AR", {
              weekday: "long", day: "numeric", month: "long", year: "numeric"
            })}
          </span>
        </CardContent>
      </Card>

      {/* Logs grouped by shift */}
      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando registros...</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByShift).map(([shiftKey, shiftLogs]) => {
            const cfg = shiftConfig[shiftKey];
            return (
              <div key={shiftKey}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-9 h-9 rounded-xl ${cfg.bg} flex items-center justify-center`}>
                    <cfg.icon className={`w-4 h-4 ${cfg.text}`} strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Turno {cfg.label}</h3>
                    <p className="text-xs text-slate-400">{cfg.time} — {shiftLogs.length} registro(s)</p>
                  </div>
                </div>

                {shiftLogs.length === 0 ? (
                  <Card className="rounded-2xl border-slate-100 shadow-sm">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-slate-400">Sin novedades registradas para este turno</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {shiftLogs.map(log => {
                      const pCfg = priorityConfig[log.priority] || priorityConfig.normal;
                      return (
                        <Card
                          key={log.id}
                          className={`rounded-2xl border-slate-100 shadow-sm ${log.priority === "urgente" ? "border-l-4 border-l-rose-400" : log.priority === "importante" ? "border-l-4 border-l-amber-400" : ""}`}
                          data-testid={`daily-log-${log.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${pCfg.bg} ${pCfg.text}`}>
                                    {pCfg.label}
                                  </Badge>
                                  {log.log_type === "residente" && (
                                    <Badge className="rounded-full text-[10px] font-semibold border-0 px-2.5 bg-violet-50 text-violet-700">
                                      <User className="w-3 h-3 mr-1" />
                                      {getResidentName(log.resident_id)}
                                    </Badge>
                                  )}
                                  <span className="text-xs text-slate-400">
                                    {new Date(log.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{log.content}</p>
                                <p className="text-xs text-slate-400 mt-2">
                                  Registrado por {log.created_by_name}
                                </p>
                              </div>
                              {canCreate && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(log.id)}
                                  className="rounded-full hover:bg-rose-50 hover:text-rose-500 w-8 h-8 flex-shrink-0"
                                  data-testid={`delete-log-${log.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
